import { compareRecordedDates } from '@/utils/recordedValues'
/**
 * User Store - Zustand
 * State management for User Management
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  User,
  UserRole,
  UserFilters,
  CreateUserInput,
  UpdateUserInput,
  UserInvitation,
  InviteUserInput,
} from '@/types/user.types'
import { DEFAULT_USER_FILTERS } from '@/types/user.types'
import { getUserService } from '@/services/users'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface UserState {
  // Data
  users: User[]
  selectedUser: User | null
  invitations: UserInvitation[]
  isLoading: boolean
  error: string | null

  // Filters
  filters: UserFilters
  searchQuery: string
  sortBy: 'name' | 'email' | 'role' | 'lastLogin' | 'createdAt'
  sortOrder: 'asc' | 'desc'

  // Pagination
  page: number
  pageSize: number

  // Selection
  selectedIds: string[]

  // UI State
  isFilterPanelOpen: boolean
}

interface UserActions {
  // CRUD Operations
  loadUsers: (tenantId: string) => Promise<void>
  addUser: (input: CreateUserInput, tenantId: string) => Promise<User>
  updateUser: (userId: string, updates: UpdateUserInput) => Promise<void>
  deleteUser: (userId: string, reason?: string) => Promise<void>
  activateUser: (userId: string) => Promise<void>
  deactivateUser: (userId: string) => Promise<void>

  // Role Management
  assignRole: (userId: string, role: UserRole) => Promise<void>

  // Invitations
  loadInvitations: (tenantId: string) => Promise<void>
  inviteUser: (
    input: InviteUserInput,
    tenantId: string,
    invitedBy: string
  ) => Promise<UserInvitation>
  cancelInvitation: (invitationId: string) => Promise<void>
  resendInvitation: (invitationId: string) => Promise<void>

  // Selection
  setSelectedUser: (userId: string | null) => void
  toggleSelectUser: (userId: string) => void
  selectAllUsers: (ids: string[]) => void
  clearSelection: () => void

  // Filters
  setFilters: (filters: Partial<UserFilters>) => void
  clearFilters: () => void
  setSearchQuery: (query: string) => void
  setSortBy: (sortBy: UserState['sortBy']) => void
  setSortOrder: (order: UserState['sortOrder']) => void

  // Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void

  // UI State
  toggleFilterPanel: () => void

  // Computed/Derived
  getFilteredUsers: () => User[]
  getPaginatedUsers: () => {
    items: User[]
    total: number
    page: number
    pageSize: number
    totalPages: number
  }

  // Reset
  resetStore: () => void
}

type UserStore = UserState & UserActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: UserState = {
  users: [],
  selectedUser: null,
  invitations: [],
  isLoading: false,
  error: null,
  filters: DEFAULT_USER_FILTERS,
  searchQuery: '',
  sortBy: 'name',
  sortOrder: 'asc',
  page: 1,
  pageSize: 10,
  selectedIds: [],
  isFilterPanelOpen: false,
}

/** Merge a user patch returned by the service into list + selected state. */
function mergeUserPatch(
  state: UserState,
  userId: string,
  patch: Partial<User>
): Pick<UserState, 'users' | 'selectedUser' | 'isLoading'> {
  return {
    users: state.users.map(u => (u.userId === userId ? { ...u, ...patch } : u)),
    selectedUser:
      state.selectedUser?.userId === userId
        ? { ...state.selectedUser, ...patch }
        : state.selectedUser,
    isLoading: false,
  }
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================
        // CRUD OPERATIONS
        // ========================================

        loadUsers: async (tenantId: string) => {
          set({ isLoading: true, error: null })
          try {
            const users = await getUserService().list(tenantId)
            set({ users, isLoading: false })
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load users',
            })
          }
        },

        addUser: async (input: CreateUserInput, tenantId: string) => {
          set({ isLoading: true, error: null })
          try {
            const newUser = await getUserService().create(input, tenantId)
            set(state => ({
              users: [newUser, ...state.users],
              isLoading: false,
            }))
            return newUser
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to add user',
            })
            throw err
          }
        },

        updateUser: async (userId: string, updates: UpdateUserInput) => {
          set({ isLoading: true, error: null })
          try {
            const patch = await getUserService().update(userId, updates)
            set(state => ({
              users: state.users.map(u => (u.userId === userId ? { ...u, ...patch } : u)),
              selectedUser:
                state.selectedUser?.userId === userId
                  ? { ...state.selectedUser, ...patch }
                  : state.selectedUser,
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to update user',
            })
            throw err
          }
        },

        deleteUser: async (userId: string, reason = 'User requested deletion') => {
          set({ isLoading: true, error: null })
          try {
            await getUserService().remove(userId, reason)
            set(state => ({
              users: state.users.filter(u => u.userId !== userId),
              selectedUser: state.selectedUser?.userId === userId ? null : state.selectedUser,
              selectedIds: state.selectedIds.filter(id => id !== userId),
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to delete user',
            })
            throw err
          }
        },

        activateUser: async (userId: string) => {
          const patch = await getUserService().setActive(userId, true)
          set(state => mergeUserPatch(state, userId, patch))
        },

        deactivateUser: async (userId: string) => {
          const patch = await getUserService().setActive(userId, false)
          set(state => mergeUserPatch(state, userId, patch))
        },

        // ========================================
        // ROLE MANAGEMENT
        // ========================================

        assignRole: async (userId: string, role: UserRole) => {
          const user = get().users.find(u => u.userId === userId)
          const patch = await getUserService().assignRole(userId, role, user?.role)
          set(state => mergeUserPatch(state, userId, patch))
        },

        // ========================================
        // INVITATIONS
        // ========================================

        loadInvitations: async (tenantId: string) => {
          set({ isLoading: true })
          try {
            const invitations = await getUserService().listInvitations(tenantId)
            set({ invitations, isLoading: false })
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load invitations',
            })
          }
        },

        inviteUser: async (input: InviteUserInput, tenantId: string, invitedBy: string) => {
          set({ isLoading: true, error: null })
          try {
            const newInvitation = await getUserService().invite(input, tenantId, invitedBy)
            set(state => ({
              invitations: [newInvitation, ...state.invitations],
              isLoading: false,
            }))
            return newInvitation
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to send invitation',
            })
            throw err
          }
        },

        cancelInvitation: async (invitationId: string) => {
          set({ isLoading: true })
          try {
            await getUserService().cancelInvitation(invitationId)
            set(state => ({
              invitations: state.invitations.map(inv =>
                inv.invitationId === invitationId ? { ...inv, status: 'cancelled' as const } : inv
              ),
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to cancel invitation',
            })
            throw err
          }
        },

        resendInvitation: async (invitationId: string) => {
          set({ isLoading: true })
          try {
            const { expiresAt } = await getUserService().resendInvitation(invitationId)
            set(state => ({
              invitations: state.invitations.map(inv =>
                inv.invitationId === invitationId
                  ? { ...inv, expiresAt, status: 'pending' as const }
                  : inv
              ),
              isLoading: false,
            }))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to resend invitation',
            })
            throw err
          }
        },

        // ========================================
        // SELECTION
        // ========================================

        setSelectedUser: (userId: string | null) => {
          if (!userId) {
            set({ selectedUser: null })
            return
          }
          const user = get().users.find(u => u.userId === userId) || null
          set({ selectedUser: user })
        },

        toggleSelectUser: (userId: string) => {
          set(state => ({
            selectedIds: state.selectedIds.includes(userId)
              ? state.selectedIds.filter(id => id !== userId)
              : [...state.selectedIds, userId],
          }))
        },

        selectAllUsers: (ids: string[]) => {
          set({ selectedIds: ids })
        },

        clearSelection: () => {
          set({ selectedIds: [] })
        },

        // ========================================
        // FILTERS
        // ========================================

        setFilters: (newFilters: Partial<UserFilters>) => {
          set(state => ({
            filters: { ...state.filters, ...newFilters },
            page: 1,
          }))
        },

        clearFilters: () => {
          set({ filters: DEFAULT_USER_FILTERS, searchQuery: '', page: 1 })
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query, page: 1 })
        },

        setSortBy: sortBy => {
          set({ sortBy })
        },

        setSortOrder: sortOrder => {
          set({ sortOrder })
        },

        // ========================================
        // PAGINATION
        // ========================================

        setPage: (page: number) => {
          set({ page })
        },

        setPageSize: (pageSize: number) => {
          set({ pageSize, page: 1 })
        },

        // ========================================
        // UI STATE
        // ========================================

        toggleFilterPanel: () => {
          set(state => ({ isFilterPanelOpen: !state.isFilterPanelOpen }))
        },

        // ========================================
        // COMPUTED/DERIVED
        // ========================================

        getFilteredUsers: () => {
          const { users, filters, searchQuery, sortBy, sortOrder } = get()

          let filtered = [...users]

          // Search
          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase()
            filtered = filtered.filter(
              u =>
                u.fullName.toLowerCase().includes(query) ||
                u.email.toLowerCase().includes(query) ||
                (u.phone && u.phone.includes(query))
            )
          }

          // Filter by role
          if (filters.role && filters.role !== 'all') {
            filtered = filtered.filter(u => u.role === filters.role)
          }

          // Filter by status
          if (filters.status && filters.status !== 'all') {
            filtered = filtered.filter(u => {
              if (filters.status === 'active') return u.isActive === true
              if (filters.status === 'inactive') return u.isActive === false
              if (filters.status === 'pending') return u.isVerified === false
              if (filters.status === 'locked') return !!u.lockedUntil
              if (filters.status === 'unknown') return u.isActive == null || u.isVerified == null
              return true
            })
          }

          // Sort
          filtered.sort((a, b) => {
            let comparison = 0
            switch (sortBy) {
              case 'name':
                comparison = a.fullName.localeCompare(b.fullName)
                break
              case 'email':
                comparison = a.email.localeCompare(b.email)
                break
              case 'role':
                comparison = a.role.localeCompare(b.role)
                break
              case 'lastLogin':
                comparison = (a.lastLoginAt || '').localeCompare(b.lastLoginAt || '')
                break
              case 'createdAt':
                return compareRecordedDates(a.createdAt, b.createdAt, sortOrder)
                break
            }
            return sortOrder === 'asc' ? comparison : -comparison
          })

          return filtered
        },

        getPaginatedUsers: () => {
          const { page, pageSize } = get()
          const filtered = get().getFilteredUsers()
          const total = filtered.length
          const totalPages = Math.ceil(total / pageSize)
          const startIndex = (page - 1) * pageSize
          const items = filtered.slice(startIndex, startIndex + pageSize)

          return { items, total, page, pageSize, totalPages }
        },

        // ========================================
        // RESET
        // ========================================

        resetStore: () => {
          set(initialState)
        },
      }),
      {
        name: 'taxhive-user-store',
        partialize: state => ({
          pageSize: state.pageSize,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
      }
    ),
    { name: 'UserStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectUsers = (state: UserStore) => state.users
export const selectSelectedUser = (state: UserStore) => state.selectedUser
export const selectInvitations = (state: UserStore) => state.invitations
export const selectIsLoading = (state: UserStore) => state.isLoading
export const selectError = (state: UserStore) => state.error
export const selectFilters = (state: UserStore) => state.filters
export const selectSearchQuery = (state: UserStore) => state.searchQuery
export const selectSelectedIds = (state: UserStore) => state.selectedIds
